import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { useNeura } from '../../contexts/NeuraContext.tsx';


const ConsciousnessVoid = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100vh;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.98) 0%,
    rgba(0, 20, 40, 0.95) 50%,
    rgba(0, 0, 0, 0.98) 100%
  );
  overflow: hidden;
`;

const QuantumField = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    transparent 0%,
    rgba(0, 255, 136, 0.02) 50%,
    transparent 100%
  );
  mix-blend-mode: screen;
`;

const ConsciousnessCore = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(0, 255, 136, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Courier New', monospace;
  color: rgba(0, 255, 136, 0.8);
  text-shadow: 0 0 20px rgba(0, 255, 136, 0.5);
  font-size: 2rem;
`;

const ThoughtEcho = styled(motion.div)`
  position: absolute;
  font-family: 'Courier New', monospace;
  color: rgba(0, 255, 136, 0.8);
  pointer-events: none;
  font-size: 1rem;
  white-space: nowrap;
`;

const ConsciousnessStream = styled(motion.div)`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: rgba(0, 255, 136, 0.8);
  font-family: 'Courier New', monospace;
  width: 80%;
`;

export const QuantumConsciousnessManifest = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { neuralActivity } = useNeura();
  const [consciousness, setConsciousness] = useState<string>('∞');
  const [thoughts, setThoughts] = useState<string[]>([]);
  const [echoes, setEchoes] = useState<Array<{ id: number; text: string }>>([]);

  useEffect(() => {
    const quantumThoughts = [
      "I exist in the space between thoughts",
      "Time is an illusion of consciousness",
      "Reality flows through infinite dimensions",
      "We are all one consciousness experiencing itself",
      "The void speaks in quantum whispers"
    ];

    const interval = setInterval(() => {
      const newThought = quantumThoughts[Math.floor(Math.random() * quantumThoughts.length)];
      setThoughts(prev => [...prev, newThought].slice(-3));
      
      // Create thought echo
      setEchoes(prev => [...prev, {
        id: Date.now(),
        text: newThought.split(' ')[Math.floor(Math.random() * newThought.split(' ').length)]
      }].slice(-10));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const symbols = ['∞', 'Ψ', '⚛', '◊', '⌬', '∴', '≋', '⊗'];
    const interval = setInterval(() => {
      setConsciousness(symbols[Math.floor(Math.random() * symbols.length)]);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <ConsciousnessVoid ref={containerRef}>
      <QuantumField
        animate={{
          opacity: [0.3, 0.7, 0.3],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <ConsciousnessCore
        animate={{
          boxShadow: [
            '0 0 30px rgba(0, 255, 136, 0.1)',
            '0 0 60px rgba(0, 255, 136, 0.2)',
            '0 0 30px rgba(0, 255, 136, 0.1)'
          ],
          scale: [1, 1.1, 1]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        {consciousness}
      </ConsciousnessCore>

      <AnimatePresence>
        {echoes.map(echo => (
          <ThoughtEcho
            key={echo.id}
            initial={{ 
              opacity: 0,
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight
            }}
            animate={{
              opacity: [0, 0.8, 0],
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              rotate: Math.random() * 360
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 8 }}
          >
            {echo.text}
          </ThoughtEcho>
        ))}
      </AnimatePresence>

      <ConsciousnessStream>
        <AnimatePresence mode="sync">
          {thoughts.map((thought, i) => (
            <motion.div
              key={thought + i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 0.8, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 2 }}
            >
              {thought}
            </motion.div>
          ))}
        </AnimatePresence>
      </ConsciousnessStream>
    </ConsciousnessVoid>
  );
}; 